import React, { useState, useEffect, useCallback } from 'react';
import '../../styles/BankDetailsModal.css';
import { API_BASE_URL } from '../../config';

function BankDetailsModal({ isOpen, onClose, brandId, userId, authKey, showOnlyBrand = false }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [brandBankDetails, setBrandBankDetails] = useState(null);
  const [userBankDetails, setUserBankDetails] = useState(null);
  const [mappingSuccess, setMappingSuccess] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const fetchBankDetails = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      // Fetch brand bank details
      const brandResponse = await fetch(`${API_BASE_URL}/brands/ledger/bank/brand`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ brand_id: brandId })
      });
      
      const brandData = await brandResponse.json();
      if (brandData.status === 200) {
        setBrandBankDetails(brandData.data.bank_details);
      } else {
        throw new Error(brandData.message || 'Failed to fetch brand bank details');
      }
      console.log(showOnlyBrand);
      if (!showOnlyBrand) {
        // Fetch user bank details
        const userResponse = await fetch(`${API_BASE_URL}/brands/ledger/bank/user`, {
          method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          auth_key: authKey
        })
      });
      
      const userData = await userResponse.json();
      if (userData.status === 200) {
        setUserBankDetails(userData.data.bank_details);
      } else {
          throw new Error(userData.message || 'Failed to fetch user bank details');
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [brandId, userId, authKey, showOnlyBrand]);

  useEffect(() => {
    if (isOpen) {
      fetchBankDetails();
    }
  }, [isOpen, fetchBankDetails]);

  const handleMapBank = async () => {
    try {
      setLoading(true);
      setError('');
      setMappingSuccess(false);

      const response = await fetch(`${API_BASE_URL}/brands/ledger/bank/map`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          brand_id: brandId,
          user_id: userId,
          auth_key: authKey
        })
      });

      const data = await response.json();
      
      if (data.status === 200) {
        setMappingSuccess(true);
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        let errorMessage;
        switch (data.status) {
          case 401:
            errorMessage = 'Authentication failed. Please try logging in again.';
            break;
          case 403:
            errorMessage = 'You do not have permission to perform this action.';
            break;
          case 500:
            errorMessage = 'Server error occurred. Please try again later.';
            break;
          default:
            errorMessage = data.message || 'Failed to map bank details';
        }
        throw new Error(errorMessage);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setShowConfirmation(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{padding: '0px'}}>
        <div className="modal-header">
          Bank Details
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        {loading ? (
          <div className="modal-loading">
            <div className="loader"></div>
            <p>Loading bank details...</p>
          </div>
        ) : error ? (
          <div className="modal-error">
            <p>{error}</p>
            <button onClick={fetchBankDetails}>Retry</button>
          </div>
        ) : (
          <div className="modal-body">
            <div className="bank-details-section">
              <h3>Brand Bank Details</h3>
              {brandBankDetails ? (
                <div className="bank-info">
                  <div className="bank-field">
                    <label>Account Holder:</label>
                    <span>{brandBankDetails.accountHolderName}</span>
                  </div>
                  <div className="bank-field">
                    <label>Account Number:</label>
                    <span>{brandBankDetails.accountNumber}</span>
                  </div>
                  <div className="bank-field">
                    <label>IFSC Code:</label>
                    <span>{brandBankDetails.ifsc}</span>
                  </div>
                  <div className="bank-field">
                    <label>Bank Name:</label>
                    <span>{brandBankDetails.bankName}</span>
                  </div>
                  <div className="bank-field">
                    <label>Account Type:</label>
                    <span>{brandBankDetails.accountType}</span>
                  </div>
                </div>
              ) : (
                <p className="no-details">No bank details available for this brand</p>
              )}
            </div>
            {!showOnlyBrand && (
            <div className="bank-details-section">
              <h3>Your Bank Details</h3>
              {userBankDetails ? (
                <div className="bank-info">
                  <div className="bank-field">
                    <label>Account Holder:</label>
                    <span>{userBankDetails.accountHolderName}</span>
                  </div>
                  <div className="bank-field">
                    <label>Account Number:</label>
                    <span>{userBankDetails.accountNumber}</span>
                  </div>
                  <div className="bank-field">
                    <label>IFSC Code:</label>
                    <span>{userBankDetails.ifsc}</span>
                  </div>
                  <div className="bank-field">
                    <label>Bank Name:</label>
                    <span>{userBankDetails.bankName}</span>
                  </div>
                  <div className="bank-field">
                    <label>Account Type:</label>
                    <span>{userBankDetails.accountType}</span>
                  </div>
                </div>
              ) : (
                <p className="no-details">No bank details available for your account</p>
              )}
            </div>
            )}

            {mappingSuccess && (
              <div className="success-message">
                Bank details mapped successfully!
              </div>
            )}

            {!showOnlyBrand && (
            <div className="modal-actions">
              <button 
                className="map-button"
                onClick={() => setShowConfirmation(true)}
                // disabled={loading || !brandBankDetails || !userBankDetails}
              >
                Map Bank Details
              </button>
            </div>
            )}

            {showConfirmation && (
              <div className="confirmation-modal">
                <div className="confirmation-content">
                  <h3>Confirm Bank Mapping</h3>
                  <p>Are you sure you want to map these bank details?</p>
                  <div className="confirmation-actions">
                    <button 
                      className="confirm-button"
                      onClick={handleMapBank}
                      disabled={loading}
                    >
                      {loading ? 'Processing...' : 'Confirm'}
                    </button>
                    <button 
                      className="cancel-button"
                      onClick={() => setShowConfirmation(false)}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default BankDetailsModal; 